import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import MobileScanInput from '@/modules/mobile/components/MobileScanInput';
import {
  RotateCcw,
  Package,
  CheckCircle2,
  ScanLine,
  AlertCircle,
  Truck
} from 'lucide-react';
import { toast } from 'sonner';
import { useDriver } from '../context/DriverContext';
import { parcelEvents } from '@/shared/services/trailService';

const API_URL = API_BASE;

const DriverReturns = () => {
  const navigate = useNavigate();
  const { activeRoute, geoLocation } = useDriver();
  const [scannedReturns, setScannedReturns] = useState([]);

  // Dynamic state for pending returns
  const [pendingReturns, setPendingReturns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch pending returns from API
  useEffect(() => {
    const fetchPendingReturns = async () => {
      try {
        const response = await fetch(`${API_URL}/api/delivery/driver/pending-returns`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setPendingReturns(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch pending returns:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPendingReturns();
  }, []);

  const handleReturnScan = async (barcode) => {
    const driverId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'driver';
    
    const pkg = pendingReturns.find(p => 
      p.id === barcode || barcode.includes(p.id) || barcode.includes(p.orderId)
    );

    if (pkg) {
      if (scannedReturns.some(s => s.id === pkg.id)) {
        toast.warning('Already Scanned', { description: pkg.id });
        return;
      }
      
      setScannedReturns(prev => [...prev, pkg]);
      
      // Emit trail event for package return
      await parcelEvents.packageReturned(
        activeRoute?.id,
        pkg.id,
        pkg.reason,
        driverId
      );
      
      if (scannedReturns.length + 1 === pendingReturns.length) {
        toast.success('All Returns Scanned', { description: 'Ready to confirm handover' });
      } else {
        toast.success('Return Scanned', { description: `${pendingReturns.length - scannedReturns.length - 1} remaining` });
      }
    } else {
      toast.error('Unknown Package', { description: 'Not in pending returns list' });
    }
  };

  const handleConfirmReturns = () => {
    toast.success('Returns Confirmed', { 
      description: `${scannedReturns.length} packages returned to warehouse` 
    });
    setScannedReturns([]);
    navigate('/driver');
  };

  return (
    <div className="p-4 space-y-4" data-testid="driver-returns">
      {/* Header */}
      <Card className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-white/20">
              <RotateCcw className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Return Packages</h2>
              <p className="text-sm opacity-90">
                {pendingReturns.length} packages to return
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {pendingReturns.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
            <h3 className="font-semibold text-lg">No Pending Returns</h3>
            <p className="text-muted-foreground">All packages delivered successfully</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Scanner */}
          <MobileScanInput
            title="Scan Return Package"
            subtitle="Scan packages for warehouse return"
            gateId="GATE_RETURN_PKG"
            barcodeType="CARTON"
            scanContext="RETURN"
            onScanSuccess={handleReturnScan}
            validateScan={async (barcode) => {
              const pkg = pendingReturns.find(p => 
                p.id === barcode || barcode.includes(p.id)
              );
              if (!pkg) return { success: false, result: 'REJECTED', message: 'Not in returns' };
              if (scannedReturns.some(s => s.id === pkg.id)) {
                return { success: false, result: 'WARNING', message: 'Already scanned' };
              }
              return { success: true, result: 'OK' };
            }}
          />

          {/* Returns List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Pending Returns
                </span>
                <Badge variant={scannedReturns.length === pendingReturns.length ? 'default' : 'outline'}>
                  {scannedReturns.length}/{pendingReturns.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {pendingReturns.map((pkg) => {
                const isScanned = scannedReturns.some(s => s.id === pkg.id);
                return (
                  <div key={pkg.id} className="px-4 py-3 border-b last:border-0 flex items-center gap-3">
                    {isScanned ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <ScanLine className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono">{pkg.id}</p>
                      <p className="text-xs text-muted-foreground">{pkg.customer}</p>
                      <Badge variant="outline" className="text-xs mt-1">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        {pkg.reason}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Confirm Button */}
          {scannedReturns.length > 0 && (
            <Button 
              onClick={handleConfirmReturns}
              className={`w-full ${
                scannedReturns.length === pendingReturns.length 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-amber-500 hover:bg-amber-600'
              }`}
              size="lg"
              data-testid="confirm-returns-btn"
            >
              <Truck className="h-5 w-5 mr-2" />
              {scannedReturns.length === pendingReturns.length 
                ? 'Confirm All Returns' 
                : `Confirm ${scannedReturns.length} Returns`
              }
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default DriverReturns;
